import React from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { useXeroConnection } from '../contexts/XeroConnectionContext';

const XeroConnectionIndicator = ({ 
  showDetails = true, 
  compact = false,
  onDisconnect = null,
  onReconnect = null,
  showActionButtons = false
}) => {
  const { 
    isConnected, 
    tenantName, 
    lastChecked, 
    isChecking, 
    error,
    refreshConnection 
  } = useXeroConnection();

  const handleRefresh = async () => {
    await refreshConnection();
  };

  const getTimeSinceLastCheck = () => {
    if (!lastChecked) return 'Never';
    
    const seconds = Math.floor((new Date() - new Date(lastChecked)) / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  // Compact version (for headers/toolbars)
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {isConnected ? (
          <div className="flex items-center gap-2 text-green-600">
            <Wifi className="w-4 h-4" />
            <span className="text-sm font-medium">Connected: {tenantName}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-600">
            <WifiOff className="w-4 h-4" />
            <span className="text-sm font-medium">Not Connected</span>
          </div>
        )}
        
        <button
          onClick={handleRefresh}
          disabled={isChecking}
          className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
          title="Refresh connection status"
        >
          <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
        </button>
        
        {showActionButtons && isConnected && onDisconnect && (
          <button
            onClick={onDisconnect}
            className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
            title="Disconnect from Xero"
          >
            Disconnect
          </button>
        )}
      </div>
    );
  }

  // Full version (for dashboards/settings)
  return (
    <div className={`rounded-lg border-2 p-4 ${
      isConnected 
        ? 'bg-green-50 border-green-300' 
        : 'bg-red-50 border-red-300'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full ${
            isConnected ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {isConnected ? (
              <Wifi className="w-6 h-6 text-green-600" />
            ) : (
              <WifiOff className="w-6 h-6 text-red-600" />
            )}
          </div>
          
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold text-lg ${
                isConnected ? 'text-green-800' : 'text-red-800'
              }`}>
                {isConnected ? 'Connected to Xero' : 'Not Connected to Xero'}
              </h3>
              {isChecking && (
                <RefreshCw className="w-4 h-4 animate-spin text-gray-500" />
              )}
            </div>
            
            {showDetails && (
              <div className="mt-2 space-y-1">
                {isConnected && tenantName && (
                  <p className="text-sm text-green-700">
                    <span className="font-medium">Organization:</span> {tenantName}
                  </p>
                )}
                
                {isConnected && (
                  <p className="text-sm text-green-700">
                    All Xero features are available.
                  </p>
                )}
                
                {!isConnected && (
                  <p className="text-sm text-red-700">
                    Please connect to Xero to access all features.
                  </p>
                )}
                
                {error && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-red-100 rounded">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                
                <p className="text-xs text-gray-600 mt-2">
                  Last checked: {getTimeSinceLastCheck()} • Auto-updates every 30s
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={isChecking}
            className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 ${
              isConnected
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
            title="Refresh connection status"
          >
            <div className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
              Refresh
            </div>
          </button>
          
          {showActionButtons && isConnected && onDisconnect && (
            <button
              onClick={onDisconnect}
              className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors bg-red-600 hover:bg-red-700 text-white"
              title="Disconnect from Xero"
            >
              Disconnect
            </button>
          )}
          
          {showActionButtons && isConnected && onReconnect && (
            <button
              onClick={onReconnect}
              className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors bg-blue-600 hover:bg-blue-700 text-white"
              title="Reconnect to Xero"
            >
              Reconnect
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default XeroConnectionIndicator;




